/**
 * Detect a US state in a state-landing-page URL and return its proper-cased name.
 *
 * Returns null when the URL doesn't fit the state-landing-page pattern. The
 * detector intentionally restricts matches to URLs containing a `180medical`
 * or `180-medical` segment, so a state name appearing in random product slugs
 * or blog titles (e.g. "/blog/kansas-city-event-recap/") does NOT trigger a
 * Service emission.
 *
 * Examples (all → "Alabama"):
 *
 *     /180-medical-alabama/
 *     /180medical-alabama
 *     /180-medical-urologic-catheter-supplier-in-alabama/
 *
 * Examples that intentionally return null:
 *
 *     /blog/kansas-tornado-2020/      // no 180medical prefix
 *     /products/california-catheter/  // no 180medical prefix
 *     /                                // no state, no 180medical segment
 */

// US states + DC, in URL slug form (lowercase, hyphenated multi-word names).
const US_STATES = [
  'alabama',
  'alaska',
  'arizona',
  'arkansas',
  'california',
  'colorado',
  'connecticut',
  'delaware',
  'district-of-columbia',
  'florida',
  'georgia',
  'hawaii',
  'idaho',
  'illinois',
  'indiana',
  'iowa',
  'kansas',
  'kentucky',
  'louisiana',
  'maine',
  'maryland',
  'massachusetts',
  'michigan',
  'minnesota',
  'mississippi',
  'missouri',
  'montana',
  'nebraska',
  'nevada',
  'new-hampshire',
  'new-jersey',
  'new-mexico',
  'new-york',
  'north-carolina',
  'north-dakota',
  'ohio',
  'oklahoma',
  'oregon',
  'pennsylvania',
  'rhode-island',
  'south-carolina',
  'south-dakota',
  'tennessee',
  'texas',
  'utah',
  'vermont',
  'virginia',
  'washington',
  'west-virginia',
  'wisconsin',
  'wyoming',
]

const ONE_EIGHTY_MEDICAL_RE = /\b180-?medical\b/

function toTitleCase(stateSlug: string): string {
  return stateSlug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function extractStateFromUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string')
    return null

  const lowered = url.toLowerCase()
  if (!ONE_EIGHTY_MEDICAL_RE.test(lowered))
    return null

  // Match longest state slugs first so e.g. "new-york" wins over "york"
  // (no current single-word state is a substring of a multi-word state, but
  // this guards against future additions).
  const sorted = [...US_STATES].sort((a, b) => b.length - a.length)
  for (const stateSlug of sorted) {
    const escaped = stateSlug.replace(/-/g, '-')
    const re = new RegExp(`(?:^|[/-])${escaped}(?:[/-]|$)`)
    if (re.test(lowered))
      return toTitleCase(stateSlug)
  }
  return null
}
